import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Image from '@/components/base/Image'
import ImageParallax from '@/components/base/ImageParallax'
import Link from '@/components/base/Link'
import Motion from '@/components/base/Motion'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'

import { sliderRevealAnimation } from './animations'
import { Section, WPImage, WPLink } from '@/types'

type SliderButtonsProps = {
  emblaApi?: EmblaCarouselType
}

const SliderButtons = ({ emblaApi }: SliderButtonsProps) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className="flex space-x-xs">
      <div>
        <button
          type="button"
          disabled={prevBtnDisabled}
          onClick={onPrevButtonClick}
          className={classNames(
            '-m-xxs p-xxs disabled:opacity-30',
            'ease-in-out transition-opacity duration-300'
          )}
          aria-label="Prev"
        >
          <SVGIcon
            symbol="arrow-left"
            className="h-[1.1rem] w-[2.2rem] stroke-pink"
          />
        </button>
      </div>
      <div>
        <button
          type="button"
          disabled={nextBtnDisabled}
          onClick={onNextButtonClick}
          className={classNames(
            '-m-xxs p-xxs disabled:opacity-30',
            'ease-in-out transition-opacity duration-300'
          )}
          aria-label="Next"
        >
          <SVGIcon
            symbol="arrow-right"
            className="h-[1.1rem] w-[2.2rem] stroke-pink"
          />
        </button>
      </div>
    </div>
  )
}

type SliderDotsProps = {
  emblaApi?: EmblaCarouselType
}

const SliderDots = ({ emblaApi }: SliderDotsProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return (
    <div className="flex space-x-xxs">
      {scrollSnaps.map((_, index) => (
        <div key={index}>
          <button
            key={index}
            type="button"
            className={classNames('-m-xxs p-xxs')}
            onClick={() => onDotButtonClick(index)}
            aria-label={`Dot-${index}`}
          >
            <span
              className={classNames(
                'block h-[1.3rem] w-[1.3rem] translate-y-[.1rem] rounded-full border-2',
                'ease-in-out transition-colors duration-300',
                {
                  'border-pink': selectedIndex !== index,
                  'border-pink/30': selectedIndex === index
                }
              )}
            />
          </button>
        </div>
      ))}
    </div>
  )
}

type SliderProps = Section &
  Partial<{
    type: 'default' | 'large' | 'background' | 'pillar'
    collection: Partial<{
      uuid: number
      image: WPImage
      title: string
      badge: string
      overline: string
      paragraph: string
      link: WPLink
    }>[]
  }>

const Slider = ({ type, collection, className, ...props }: SliderProps) => {
  const [ref, emblaApi] = useEmblaCarousel({ align: 'start' })

  return (
    <Motion animation={sliderRevealAnimation}>
      {(motionRef) => (
        <section
          ref={motionRef}
          className={classNames('overflow-hidden', className)}
          {...props}
        >
          <Container className="flex flex-col space-y-sm">
            <div className="-mb-[.5rem] flex items-center space-x-lg">
              <SliderButtons emblaApi={emblaApi} />
              <SliderDots emblaApi={emblaApi} />
            </div>
            <div ref={ref}>
              <div
                className={classNames('-ml-[2rem] flex md:-ml-[3rem]', {
                  'pb-4xl': type === 'pillar'
                })}
              >
                {collection?.map(
                  ({
                    uuid,
                    image,
                    badge,
                    overline,
                    title,
                    paragraph,
                    link
                  }) => {
                    return (
                      <div
                        key={`slide-${uuid}`}
                        className={classNames(
                          'min-w-0 flex-shrink-0 flex-grow-0 basis-[80%] pl-[2rem] md:pl-[3rem]',
                          {
                            'md:basis-1/2 xl:basis-1/3':
                              type === 'default' ||
                              type === 'background' ||
                              type === 'pillar',
                            'md:basis-[60%] lg:basis-[55%]': type === 'large'
                          }
                        )}
                        data-motion-slide
                      >
                        {type === 'background' ? (
                          <div className="aspect-h-[360] aspect-w-[262] md:aspect-h-[420] md:aspect-w-[393]">
                            <div
                              className={classNames(
                                'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-xs before:bg-blue',
                                'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:rounded-xs after:bg-black/40'
                              )}
                            >
                              <ImageParallax fill className="rounded-xs">
                                {(ref) => (
                                  <Image
                                    fill
                                    ref={ref}
                                    size="large"
                                    loading="lazy"
                                    source={image}
                                    alt={image?.alt}
                                    className="rounded-xs"
                                  />
                                )}
                              </ImageParallax>
                            </div>
                            <div className="flex items-end px-xs pb-sm pt-xs">
                              <div className="grid gap-y-sm">
                                {badge ? (
                                  <div className="flex">
                                    <span className="inline-flex rounded-full bg-white px-[1rem] pb-[.5rem] pt-[.7rem]">
                                      <Typography
                                        variant="small"
                                        className="uppercase text-pink"
                                      >
                                        {badge}
                                      </Typography>
                                    </span>
                                  </div>
                                ) : null}

                                {title ? (
                                  <Typography
                                    variant="h6"
                                    component="h6"
                                    className="text-white"
                                  >
                                    {title}
                                  </Typography>
                                ) : null}

                                {paragraph ? (
                                  <Typography
                                    variant="body"
                                    paragraph
                                    className="text-white"
                                  >
                                    {paragraph}
                                  </Typography>
                                ) : null}

                                {link ? (
                                  <div className="flex">
                                    <Link
                                      href={link?.url || '/'}
                                      target={link?.target}
                                      className="text-pink"
                                      textClassName="text-white"
                                    >
                                      {link?.title}
                                    </Link>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ) : type === 'pillar' ? (
                          <div
                            className={classNames(
                              'relative h-full',
                              'before:absolute before:left-0 before:top-0 before:z-0 before:h-full before:w-full before:rounded-xxs before:shadow-3xl'
                            )}
                          >
                            <div className="relative z-10 h-full rounded-xxs bg-white p-sm">
                              <div className="grid gap-y-sm">
                                {title ? (
                                  <Typography
                                    variant="h6"
                                    component="h6"
                                    className="text-blue"
                                  >
                                    {title}
                                  </Typography>
                                ) : null}

                                {paragraph ? (
                                  <Typography
                                    variant="body"
                                    paragraph
                                    className="text-neutral-20"
                                  >
                                    {paragraph}
                                  </Typography>
                                ) : null}

                                {link ? (
                                  <div className="flex">
                                    <Link
                                      href={link?.url || '/'}
                                      target={link?.target}
                                      className="text-pink"
                                      textClassName="text-pink"
                                    >
                                      {link?.title}
                                    </Link>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid gap-y-sm">
                            <div
                              className={classNames(
                                'aspect-h-[180] aspect-w-[262]',
                                {
                                  'lg:aspect-h-[221] lg:aspect-w-[393]':
                                    type === 'default',
                                  'lg:aspect-h-[480] lg:aspect-w-[655]':
                                    type === 'large'
                                }
                              )}
                            >
                              <ImageParallax fill className="rounded-xxs">
                                {(ref) => (
                                  <Image
                                    fill
                                    ref={ref}
                                    size="large"
                                    loading="lazy"
                                    source={image}
                                    alt={image?.alt}
                                    className="rounded-xxs"
                                  />
                                )}
                              </ImageParallax>
                            </div>
                            {overline ? (
                              <Typography
                                variant="small"
                                paragraph
                                className="text-neutral-20"
                              >
                                {overline}
                              </Typography>
                            ) : null}

                            {title ? (
                              <Typography
                                variant="h5"
                                component="h5"
                                className="text-blue"
                              >
                                {title}
                              </Typography>
                            ) : null}

                            {paragraph ? (
                              <Typography
                                variant="body"
                                paragraph
                                className="text-neutral-20"
                              >
                                {paragraph}
                              </Typography>
                            ) : null}

                            {link ? (
                              <div>
                                <Button
                                  href={link?.url}
                                  link
                                  variant="contained"
                                >
                                  {link?.title}
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )
                  }
                )}
              </div>
            </div>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default Slider
